import { secondsToParts } from './durationParts';

type params = {
  seconds: number;
  alwaysIncludeSeconds?: boolean;
  hideZeroSeconds?: boolean;
  shortFormat?: boolean;
  includeDays?: boolean;
};

export const secondsToTimeString = ({
  seconds,
  alwaysIncludeSeconds = true,
  hideZeroSeconds = false,
  shortFormat = false,
  includeDays = true
}: params) => {
  if (seconds <= 0) {
    return hideZeroSeconds ? '' : shortFormat ? '0s' : '0 sec';
  }

  const { d: days, h: hours, m: minutes, s: remainingSeconds } = secondsToParts({ seconds, includeDays });

  const daysString = formatDays(days, shortFormat);
  const hoursString = formatHours(hours, shortFormat);
  const minutesString = formatMinutes(minutes, shortFormat);
  const secondsString =
    remainingSeconds > 0 || (days === 0 && hours === 0 && minutes === 0)
      ? formatSeconds(remainingSeconds, shortFormat)
      : '';

  if (alwaysIncludeSeconds) {
    return (daysString + hoursString + minutesString + secondsString).trim();
  }
  if (days > 0) {
    return (daysString + hoursString).trim();
  }
  if (hours > 0) {
    return (hoursString + minutesString).trim();
  }
  if (minutes > 0) {
    return minutesString.trim();
  }
  return secondsString.trim();
};

const formatDays = (days: number, shortFormat: boolean) => {
  if (days <= 0) {
    return '';
  }
  return shortFormat ? `${days}d ` : `${days} day${days === 1 ? '' : 's'} `;
};

const formatHours = (hours: number, shortFormat: boolean) => {
  if (hours <= 0) {
    return '';
  }
  return shortFormat ? `${hours}h ` : `${hours} hour${hours === 1 ? '' : 's'} `;
};

const formatMinutes = (minutes: number, shortFormat: boolean) => {
  if (minutes <= 0) {
    return '';
  }
  return shortFormat ? `${minutes}m ` : `${minutes} min `;
};

const formatSeconds = (seconds: number, shortFormat: boolean) => {
  return shortFormat ? `${seconds}s` : `${seconds} sec`;
};

/**
 * Whole minutes as a duration label using the same rules as {@link secondsToTimeString}
 * with `alwaysIncludeSeconds: false` (e.g. widen totals, “N more …” hints).
 * Zero minutes is shown as `0 min` (not `0 sec`).
 */
export const formatWholeMinutesAsTimeString = (wholeMinutes: number): string => {
  if (wholeMinutes <= 0) {
    return '0 min';
  }
  return secondsToTimeString({ seconds: wholeMinutes * 60, alwaysIncludeSeconds: false });
};
